import * as Popover from "@radix-ui/react-popover";

import { AutocompleteResult, AutocompleteSectionId, RecentSelection, SearchAutocompleteSection } from "./types";
import {
  EmptyStateContent,
  LoadingIndicator,
  RecentSelectionsContent,
  SearchIconLoadingIndicator,
  SearchResultsContent,
  SectionButton,
} from "./components";
import { clearGlobalSearch, updateGlobalSearch } from "src/js/stores/map/actions/updateSearch";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { ErrorWithToast } from "./ErrorWithToast";
import Feature from "src/js/stores/user/Feature";
import { LtIconKey } from "react-migration/domains/constraints/designation/style/icons";
import clsx from "clsx";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { isDefined } from "react-migration/lib/util/isDefined";
import { logEventV2 } from "react-migration/lib/util/logEventV2";
import { mapStore } from "src/js/stores/map/store";
import { showError } from "src/js/stores/user/actions/addNotification";
import { updateSelection } from "src/js/stores/map/actions/updateSelection";
import { useLocalStorage } from "usehooks-ts";
import { useSnapshot } from "valtio/react";

type GlobalSearchBaseProps = {
  className?: string;
  placeholder?: string;
  inputClassName?: string;
  icon?: LtIconKey;
  sections: SearchAutocompleteSection[];
  sectionsWhitelist?: AutocompleteSectionId[];

  // @deprecated used to place pin in old location search, as well as to log location search events
  onSearchLocationChange?(location: null | { geometry: any; place: any }): void;
};

function GlobalSearchBase({
  className,
  inputClassName,
  placeholder = "Search for a location or title",
  icon = "lt-search-line",
  sections: unfilteredSections,
  sectionsWhitelist,
  onSearchLocationChange,
}: GlobalSearchBaseProps) {
  const isUSUser = hasFeature(Feature.usAccess);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  const highlightedRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null);

  const { googleMap, globalSearch } = useSnapshot(mapStore);

  const [isHandlingSelection, setIsHandlingSelection] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<AutocompleteSectionId | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [loadingSections, setLoadingSections] = useState<Set<AutocompleteSectionId>>(new Set());
  const [isMouseActive, setIsMouseActive] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [, setSectionErrors] = useState<Map<AutocompleteSectionId, Error>>(new Map());

  const [searchResults, setSearchResults] = useState(new Map<AutocompleteSectionId, AutocompleteResult[]>());

  const [recentSelections, setRecentSelections] = useLocalStorage<RecentSelection[]>("recent-searches", []);

  useLayoutEffect(() => {
    const inputElement = inputRef.current;
    if (!inputElement) return;
    const pending = pendingSelectionRef.current;
    if (pending) {
      const len = inputElement.value.length;
      inputElement.selectionStart = Math.min(pending.start, len);
      inputElement.selectionEnd = Math.min(pending.end, len);
    }
  });

  const sections = useMemo(() => {
    if (!!sectionsWhitelist?.length) {
      return unfilteredSections.filter((section) => sectionsWhitelist.includes(section.id));
    }

    return unfilteredSections;
  }, [unfilteredSections, sectionsWhitelist]);

  // Hydrate recent selections with their sections, filtering out any that don't have matching sections
  const hydratedRecentSelections = useMemo(
    () =>
      recentSelections
        .map((selection) => {
          const section = sections.find((s) => s.id === selection.sectionId);
          return section ? { ...selection, section } : null;
        })
        .filter(isDefined),
    [recentSelections, sections]
  );

  // Scroll highlighted item into view when it changes
  useEffect(() => {
    if (highlightedRef.current) {
      highlightedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Fetch search results when search term changes
  useEffect(
    function fetchAutocompleteSectionResults() {
      let cancelled = false;

      // Abort current search if any
      const abortCurrentSearch = () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      };

      const fetchSectionResults = async (section: SearchAutocompleteSection, searchTerm: string) => {
        if (cancelled) return;

        setLoadingSections((prev) => new Set(prev).add(section.id));
        try {
          const sectionResults = await section.fetchResults(searchTerm, abortControllerRef.current!.signal);

          if (!cancelled) {
            setSearchResults((prev) => new Map(prev).set(section.id, sectionResults));
          }
        } catch (error) {
          if (!cancelled) {
            setSectionErrors((prev) => new Map(prev).set(section.id, error as Error));
          }
        } finally {
          if (!cancelled) {
            setLoadingSections((prev) => {
              const next = new Set(prev);
              next.delete(section.id);
              return next;
            });
          }
        }
      };

      onSearchLocationChange?.(null);
      setSectionErrors(new Map());
      setHighlightedIndex(0);
      setSelectedSectionId(null);

      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        abortControllerRef.current = new AbortController();

        for (const section of sections) {
          fetchSectionResults(section, globalSearch);
        }
      }, 300);

      return () => {
        cancelled = true;
        abortCurrentSearch();
        clearTimeout(debounceTimeoutRef.current);
      };
    },
    [globalSearch, sections, onSearchLocationChange]
  );

  const handleSelect = useCallback(
    async (
      result: AutocompleteResult,
      section: SearchAutocompleteSection,
      options?: { skipRecentUpdate?: boolean }
    ) => {
      if (!googleMap) return;

      try {
        clearGlobalSearch();
        inputRef.current?.blur();
        setIsHandlingSelection(true);

        const eventProperties = await section.onSelectionHandler(result, {
          map: googleMap,
          setSelection: updateSelection,
          onSearchLocationChange,
        });

        logEventV2({
          name: "Location Search",
          properties: {
            type: section.id,
            id: result.id,
            recentSearch: options?.skipRecentUpdate || false,
            country: isUSUser ? "US" : "UK",
            ...eventProperties,
          },
        });

        if (!options?.skipRecentUpdate) {
          const isDuplicate = recentSelections.some(
            (selection) => selection.result.id === result.id && selection.sectionId === section.id
          );

          if (!isDuplicate) {
            setRecentSelections((prev) => {
              const newSelection = { result, sectionId: section.id };
              return [newSelection, ...prev].slice(0, 5);
            });
          }
        }
      } catch (e) {
        showError({
          id: "global-search_selection-error",
          title: "Unable to make selection",
          description: (e as ErrorWithToast)?.toast,
          duration: 10_000,
        });

        console.error(`Failed to handle selection for ${section.title} with id ${result.id}`, e);
      } finally {
        setIsHandlingSelection(false);
      }
    },
    [googleMap, onSearchLocationChange, recentSelections, setRecentSelections, isUSUser]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
        pendingSelectionRef.current = null;
      }

      if (!sections.length) return;

      if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
        e.preventDefault();
      }

      const recentSelectionItems = hydratedRecentSelections.map(({ result, section }) => ({
        result,
        section,
      }));

      const searchResultItems = sections.flatMap((section) => {
        const results = searchResults.get(section.id) || [];
        return results.map((result) => ({ result, section }));
      });

      const usingRecentSection = !globalSearch && hydratedRecentSelections.length > 0;
      const currentItems = usingRecentSection ? recentSelectionItems : searchResultItems;

      if (currentItems.length === 0) return;

      // Set mouse inactive when using keyboard
      setIsMouseActive(false);

      if (e.key === "ArrowDown") {
        setHighlightedIndex((prev) => (prev + 1) % currentItems.length);
      } else if (e.key === "ArrowUp") {
        setHighlightedIndex((prev) => (prev - 1 + currentItems.length) % currentItems.length);
      } else if (e.key === "Enter") {
        const item = currentItems[highlightedIndex];
        if (item) {
          handleSelect(item.result, item.section, { skipRecentUpdate: usingRecentSection });
        }
      }
    },
    [searchResults, highlightedIndex, handleSelect, sections, globalSearch, hydratedRecentSelections]
  );

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = e.target;
    pendingSelectionRef.current = {
      start: selectionStart ?? 0,
      end: selectionEnd ?? 0,
    };
    updateGlobalSearch(value);
  }, []);

  // Filter sections based on selection
  const visibleSections = selectedSectionId ? sections.filter((section) => section.id === selectedSectionId) : sections;

  return (
    <Popover.Root
      open={isFocused}
      onOpenChange={(open) => {
        if (open) {
          setHighlightedIndex(0);
        } else {
          // Clear all highlighted state when popup closes
          setHighlightedIndex(0);
          setSelectedSectionId(null);
          setIsMouseActive(false);
        }
      }}
    >
      <div className={clsx("flex flex-1 mx-auto justify-center w-full", className)}>
        <div className="w-full px-1 md:w-auto md:px-0">
          <SearchIconLoadingIndicator icon={icon} loading={isHandlingSelection} className="absolute" />
          <Popover.PopoverAnchor>
            <input
              id="global-search__input"
              data-testid="global-search__input"
              ref={inputRef}
              type="search"
              autoComplete="off"
              placeholder={placeholder}
              value={globalSearch}
              onChange={handleSearchChange}
              onMouseDown={() => {
                pendingSelectionRef.current = null;
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                pendingSelectionRef.current = null;
                // Only blur if the related target is not within the popup
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (!relatedTarget || !relatedTarget.closest("[data-popover-content]")) {
                  setIsFocused(false);
                }
              }}
              disabled={isHandlingSelection}
              className={clsx(
                "rounded-lg border px-1 pl-9 md:pr-3 grow w-full md:w-[330px]",
                "bg-background-input border-border-action placeholder:text-text-base-tertiary",
                "h-12 md:h-9 placeholder:text-md md:placeholder:text-sm text-md md:text-sm",
                "shadow-map-button md:shadow-xs",
                {
                  "cursor-not-allowed": isHandlingSelection,
                },
                inputClassName
              )}
            />
          </Popover.PopoverAnchor>
        </div>
      </div>
      <Popover.Portal>
        <Popover.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onMouseDown={(e) => e.preventDefault()}
          onMouseMove={() => setIsMouseActive(true)}
          data-popover-content
          className={clsx(
            "z-30 bg-white drop-shadow-lg rounded-lg flex flex-col p-4",
            "w-[calc(100vw-20px)] md:w-[512px] max-h-[250px] md:max-h-[320px] md:h-auto mx-[10px] md:m-0 "
          )}
          sideOffset={16}
        >
          {/* Section buttons, loading indicator */}
          <div className="relative pb-4 border-b border-border-base-divider">
            <div className="flex gap-4">
              <SectionButton
                active={!selectedSectionId}
                disabled={!selectedSectionId}
                onClick={() => {
                  setSelectedSectionId(null);
                  setHighlightedIndex(0);
                }}
              >
                All
              </SectionButton>
              {sections.map((section) => {
                const hasResults = !!searchResults.get(section.id)?.length;

                return (
                  <SectionButton
                    key={section.id}
                    disabled={!hasResults}
                    active={selectedSectionId === section.id}
                    onClick={() => {
                      if (hasResults) {
                        setSelectedSectionId(section.id);
                        setHighlightedIndex(0);
                      }
                    }}
                  >
                    {section.title}
                  </SectionButton>
                );
              })}
            </div>
            <div className="absolute top-0 right-0">
              <LoadingIndicator show={loadingSections.size > 0} />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" style={{ scrollPadding: 32 }}>
            {globalSearch ? (
              !searchResults.size ? (
                <EmptyStateContent message="Searching…" />
              ) : Array.from(searchResults.values()).flat().length > 0 ? (
                <SearchResultsContent
                  visibleSections={visibleSections}
                  searchResults={searchResults}
                  highlightedIndex={highlightedIndex}
                  isMouseActive={isMouseActive}
                  handleSelect={handleSelect}
                  highlightedRef={highlightedRef}
                />
              ) : (
                <EmptyStateContent message="No results found" />
              )
            ) : (
              <RecentSelectionsContent
                hydratedRecentSelections={hydratedRecentSelections}
                highlightedIndex={highlightedIndex}
                isMouseActive={isMouseActive}
                handleSelect={handleSelect}
                highlightedRef={highlightedRef}
                clearRecentSelections={() => setRecentSelections([])}
              />
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export type GlobalSearchProps = Omit<GlobalSearchBaseProps, "sections">;

type CreateLocationSearchArgs = {
  sections: SearchAutocompleteSection[];
  placeholder?: string;
};

export function createLocationSearch({ sections, placeholder }: CreateLocationSearchArgs) {
  return function LocationSearchWithSections(props: GlobalSearchProps) {
    return <GlobalSearchBase sections={sections} placeholder={placeholder} {...props} />;
  };
}
